/**
 * <xp-collection-tools> — collection toolbar extras and the theme-side filters:
 * grid/list view toggle, an instant "search this page" box, and category/brand/sale checkboxes.
 *
 * Markup contract:
 *   [data-view-button="grid|list"]  toggle buttons (aria-pressed is kept in sync)
 *   input[data-collection-filter]   text box; it belongs to a separate store-search form
 *                                   via its `form` attribute, so Enter searches the store
 *   input[data-facet-option="<facet>"] filter checkboxes anywhere on the page (sidebar or drawer);
 *                                   value matches li[data-<facet>] on product cards; OR within a
 *                                   facet, AND across facets
 *   [data-facet-count="<facet>"]    optional badge showing how many values are selected
 *   data-results="<id>"             element that receives data-view and holds the grid
 *                                   (outside the partial, so it survives live updates)
 *   data-empty="<id>"               hidden note shown when nothing on the page matches
 *
 * Facet and search events are stopped in the capture phase so the surrounding
 * <s-live-form> does not refetch; they only show or hide cards already on the page.
 */
const STORAGE_KEY = 'xp-collection-view';

class XpCollectionTools extends HTMLElement {
  #abort = null;
  /** @type {Map<string, Set<string>>} */
  #facets = new Map();
  #terms = [];

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#applyView(this.#readView());

    this.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-view-button]') : null;
      if (!button || !this.contains(button)) return;
      const view = button.getAttribute('data-view-button');
      this.#applyView(view);
      try { localStorage.setItem(STORAGE_KEY, view); } catch { /* storage unavailable */ }
    }, { signal });

    const input = this.querySelector('input[data-collection-filter]');
    if (input) {
      input.addEventListener('input', (event) => {
        event.stopPropagation();
        this.#terms = input.value.trim().toLowerCase().split(/\s+/).filter(Boolean);
        this.#apply();
      }, { signal });
      input.addEventListener('change', (event) => event.stopPropagation(), { signal });
    }

    const onFacet = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || !target.hasAttribute('data-facet-option')) return;
      event.stopPropagation();
      if (event.type !== 'change') return;
      const facet = target.getAttribute('data-facet-option');
      if (!this.#facets.has(facet)) this.#facets.set(facet, new Set());
      const chosen = this.#facets.get(facet);
      if (target.checked) chosen.add(target.value);
      else chosen.delete(target.value);
      this.#syncFacetInputs();
      this.#apply();
    };
    document.addEventListener('change', onFacet, { capture: true, signal });
    document.addEventListener('input', onFacet, { capture: true, signal });

    // Live filter/sort swaps re-render the grid and filter list; restore ticks and re-apply.
    document.addEventListener('s-live-form:updated', () => {
      this.#syncFacetInputs();
      this.#apply();
    }, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  #readView() {
    try { return localStorage.getItem(STORAGE_KEY) === 'list' ? 'list' : 'grid'; } catch { return 'grid'; }
  }

  #applyView(view) {
    const results = document.getElementById(this.dataset.results);
    if (results) results.setAttribute('data-view', view);
    for (const button of this.querySelectorAll('[data-view-button]')) {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-view-button') === view));
    }
  }

  #syncFacetInputs() {
    for (const box of document.querySelectorAll('input[data-facet-option]')) {
      box.checked = this.#facets.get(box.getAttribute('data-facet-option'))?.has(box.value) ?? false;
    }
    for (const badge of document.querySelectorAll('[data-facet-count]')) {
      const size = this.#facets.get(badge.getAttribute('data-facet-count'))?.size ?? 0;
      badge.textContent = String(size);
      badge.classList.toggle('hidden', size === 0);
      badge.classList.toggle('inline-flex', size > 0);
    }
  }

  #activeFacetCount() {
    let total = 0;
    for (const chosen of this.#facets.values()) total += chosen.size;
    return total;
  }

  #apply() {
    const results = document.getElementById(this.dataset.results);
    if (!results) return;
    let shown = 0;
    for (const item of results.querySelectorAll('[data-product-title]')) {
      const title = item.getAttribute('data-product-title') || '';
      const termsOk = this.#terms.every((term) => title.includes(term));
      let facetsOk = true;
      for (const [facet, chosen] of this.#facets) {
        if (chosen.size === 0) continue;
        if (!chosen.has(item.getAttribute(`data-${facet}`) || '')) { facetsOk = false; break; }
      }
      item.hidden = !(facetsOk && termsOk);
      if (!item.hidden) shown += 1;
    }
    const empty = document.getElementById(this.dataset.empty);
    if (empty) empty.hidden = shown > 0 || (this.#terms.length === 0 && this.#activeFacetCount() === 0);
  }
}

if (!customElements.get('xp-collection-tools')) customElements.define('xp-collection-tools', XpCollectionTools);
